/**
 * GeoNames API Service
 * Free API for fetching cities data worldwide
 *
 * Note: Replace 'demo' username with your own from http://www.geonames.org/login
 * The demo account has limited requests per hour
 */

const GEONAMES_USERNAME = 'demo'; // Replace with your username after registration
const GEONAMES_BASE_URL = 'https://secure.geonames.org'; // Using HTTPS secure endpoint

// Cache для городов по странам
const citiesCache = new Map();

// Mapping country codes to country names in Russian (для fallback на локальную базу)
const countryCodeToName = {
  RU: 'Россия',
  BY: 'Беларусь',
  UA: 'Украина',
  KZ: 'Казахстан',
  UZ: 'Узбекистан',
  AZ: 'Азербайджан',
  AM: 'Армения',
  GE: 'Грузия',
  MD: 'Молдова',
  LV: 'Латвия',
  LT: 'Литва',
  EE: 'Эстония',
  PL: 'Польша',
  CZ: 'Чехия',
  SK: 'Словакия',
  HU: 'Венгрия',
  RO: 'Румыния',
  BG: 'Болгария',
  RS: 'Сербия',
  HR: 'Хорватия',
  SI: 'Словения',
  ME: 'Черногория',
  MK: 'Северная Македония',
  AL: 'Албания',
  GR: 'Греция',
  TR: 'Турция',
  CY: 'Кипр',
  IL: 'Израиль',
  AE: 'ОАЭ',
  TH: 'Таиланд',
  VN: 'Вьетнам',
  IN: 'Индия',
  KR: 'Южная Корея',
  AU: 'Австралия',
  NZ: 'Новая Зеландия',
  BR: 'Бразилия',
  AR: 'Аргентина',
  MX: 'Мексика',
  EG: 'Египет',
  ZA: 'ЮАР',
  NG: 'Нигерия',
  KE: 'Кения',
  MA: 'Марокко',
  TN: 'Тунис',
  DZ: 'Алжир',
  US: 'США',
  CA: 'Канада',
  DE: 'Германия',
  FR: 'Франция',
  GB: 'Великобритания',
  JP: 'Япония',
  CN: 'Китай',
  IT: 'Италия',
  ES: 'Испания',
  PT: 'Португалия',
  NL: 'Нидерланды',
  BE: 'Бельгия',
  AT: 'Австрия',
  CH: 'Швейцария',
  SE: 'Швеция',
  NO: 'Норвегия',
  DK: 'Дания',
  FI: 'Финляндия',
};

/**
 * Получить ВСЕ города для указанной страны (включая маленькие)
 * @param {string} countryCode - ISO код страны (например, 'RU', 'BY')
 * @returns {Promise<Array>} - Массив ВСЕХ городов
 */
export const getCitiesByCountry = async (countryCode) => {
  // Проверяем кэш
  if (citiesCache.has(countryCode)) {
    console.log(`✓ Loaded from cache: ${citiesCache.get(countryCode).length} cities`);
    return citiesCache.get(countryCode);
  }

  try {
    // GeoNames API имеет лимит 1000 результатов за запрос
    // Делаем несколько запросов для получения ВСЕХ городов
    let allCities = [];
    let startRow = 0;
    const maxRowsPerRequest = 1000;
    let hasMore = true;

    console.log(`Loading ALL cities for ${countryCode}...`);

    while (hasMore && startRow < 5000) {
      // Максимум 5000 городов (безопасность)
      const response = await fetch(
        `${GEONAMES_BASE_URL}/searchJSON?country=${countryCode}&featureClass=P&featureCode=PPL&featureCode=PPLA&featureCode=PPLA2&featureCode=PPLA3&featureCode=PPLA4&featureCode=PPLC&featureCode=PPLG&featureCode=PPLS&maxRows=${maxRowsPerRequest}&startRow=${startRow}&username=${GEONAMES_USERNAME}&lang=ru`,
      );

      if (!response.ok) {
        throw new Error(`GeoNames API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.status) {
        console.error('GeoNames API error:', data.status.message);
        throw new Error(data.status.message);
      }

      const cities = data.geonames.map((city) => ({
        name: city.name,
        toponymName: city.toponymName,
        lat: parseFloat(city.lat),
        lng: parseFloat(city.lng),
        population: city.population || 0,
        countryCode: city.countryCode,
        adminName1: city.adminName1, // Region/Oblast
        featureCode: city.fcode, // Тип населенного пункта
      }));

      allCities = allCities.concat(cities);

      console.log(`Loaded batch: ${cities.length} cities (total: ${allCities.length})`);

      // Если получили меньше maxRows, значит это последняя страница
      if (cities.length < maxRowsPerRequest) {
        hasMore = false;
      } else {
        startRow += maxRowsPerRequest;
      }
    }

    // Удаляем дубликаты по названию
    const uniqueCities = Array.from(new Map(allCities.map((city) => [city.name, city])).values());

    // Сортируем по населению (большие города сверху)
    uniqueCities.sort((a, b) => b.population - a.population);

    console.log(`✓ Total unique cities loaded for ${countryCode}: ${uniqueCities.length}`);

    // Сохраняем в кэш
    citiesCache.set(countryCode, uniqueCities);

    return uniqueCities;
  } catch (error) {
    console.error('Error fetching cities from GeoNames:', error);
    return [];
  }
};

/**
 * Поиск городов по названию
 * @param {string} query - Поисковый запрос
 * @param {string} countryCode - Опциональный ISO код страны для фильтрации
 * @returns {Promise<Array>} - Массив городов
 */
export const searchCities = async (query, countryCode = null) => {
  if (!query || query.length < 2) {
    return [];
  }

  try {
    let url = `${GEONAMES_BASE_URL}/searchJSON?name_startsWith=${encodeURIComponent(
      query,
    )}&featureClass=P&maxRows=50&username=${GEONAMES_USERNAME}&lang=ru`;

    if (countryCode) {
      url += `&country=${countryCode}`;
    }

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`GeoNames API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.status) {
      console.error('GeoNames API error:', data.status.message);
      throw new Error(data.status.message);
    }

    return data.geonames.map((city) => ({
      name: city.name,
      toponymName: city.toponymName,
      lat: parseFloat(city.lat),
      lng: parseFloat(city.lng),
      population: city.population,
      countryCode: city.countryCode,
      countryName: countryCodeToName[city.countryCode] || city.countryName,
      adminName1: city.adminName1,
    }));
  } catch (error) {
    console.error('Error searching cities:', error);
    return [];
  }
};

/**
 * Альтернативный бесплатный API - REST Countries для получения списка стран
 * https://restcountries.com/
 */
export const getAllCountries = async () => {
  try {
    const response = await fetch('https://restcountries.com/v3.1/all');
    const data = await response.json();

    return data
      .map((country) => ({
        name: country.name.common,
        nativeName: country.name.nativeName,
        code: country.cca2,
        capital: country.capital?.[0],
        region: country.region,
        population: country.population,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    console.error('Error fetching countries:', error);
    return [];
  }
};

/**
 * Fallback: загрузить города из локального файла worldcities.json
 */
export const getCitiesFromLocalFile = async (countryCode) => {
  // Проверяем кэш
  const cacheKey = `local_${countryCode}`;
  if (citiesCache.has(cacheKey)) {
    return citiesCache.get(cacheKey);
  }

  try {
    const response = await fetch('/pre/worldcities.json');
    if (!response.ok) {
      throw new Error(`Failed to load local cities: ${response.status}`);
    }

    const allCities = await response.json();

    // Находим название страны по коду
    const countryName = Object.keys(countryCodeToName).find((code) => code === countryCode);
    const russianCountryName = countryCodeToName[countryCode];

    // Фильтруем города по стране
    const cities = allCities
      .filter((city) => city.country === russianCountryName)
      .map((city) => ({
        name: city.city,
        toponymName: city.city,
        lat: 0, // В локальной базе нет координат
        lng: 0,
        population: 0,
        countryCode: countryCode,
      }));

    citiesCache.set(cacheKey, cities);
    return cities;
  } catch (error) {
    console.error('Error loading cities from local file:', error);
    return [];
  }
};

export const getCitiesFromTxt = async (countryCode) => {
  const cacheKey = `txt_${countryCode}`;

  if (citiesCache.has(cacheKey)) {
    return citiesCache.get(cacheKey);
  }

  try {
    let filePath;

    if (countryCode === 'BY') {
      filePath = '/pre/cities_by.txt';
    } else if (countryCode === 'RU') {
      filePath = '/pre/cities_ru.txt';
    } else {
      return [];
    }

    const response = await fetch(filePath);

    if (!response.ok) {
      throw new Error('Failed to load txt file');
    }

    const text = await response.text();

    const cities = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((name) => ({
        name,
        toponymName: name,
        lat: 0,
        lng: 0,
        population: 0,
        countryCode,
      }));

    citiesCache.set(cacheKey, cities);

    return cities;
  } catch (error) {
    console.error('Error loading cities from txt:', error);
    return [];
  }
};

/**
 * Комбинированная функция - пробует несколько источников по очереди
 */
export const getCitiesWithFallback = async (countryCode) => {
  console.log(`Loading cities for ${countryCode}...`);

  let cities = await getCitiesFromTxt(countryCode);
  if (cities.length > 0) {
    console.log(`✓ Loaded ${cities.length} cities from txt`);
    return cities;
  }

  // 1. Пробуем GeoNames API (бесплатный, но требует регистрации)
  cities = await getCitiesByCountry(countryCode);

  if (cities.length > 0) {
    console.log(`✓ Loaded ${cities.length} cities from GeoNames API`);
    return cities;
  }

  // 2. Если не получилось, используем локальный файл
  console.log('GeoNames API unavailable, trying local database...');
  cities = await getCitiesFromLocalFile(countryCode);

  if (cities.length > 0) {
    console.log(`✓ Loaded ${cities.length} cities from local database`);
    return cities;
  }

  console.warn(`⚠ No cities found for ${countryCode}`);
  return [];
};
